import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import Box from "@mui/material/Box";
import { clipRmsG, formatG } from "../utils/dmicGain";

const MIN_G = 0.001;

// ECharts gauges are linear, so the gauge runs on log10(g) and labels convert back to g
const toLog = (g) => Math.log10(Math.max(g || 0, MIN_G));

// totalGainDb: dmic_gain * 12 + adc_vol dB; the scale tops out at the sensor's clip level
function VibrationGauge({ deviceName, gValue, totalGainDb }) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  // Round so tiny gain changes do not rebuild the chart
  const maxG = Number(Math.max(clipRmsG(totalGainDb || 0), MIN_G * 10).toPrecision(2));

  useEffect(() => {
    const chartInstance = echarts.init(chartRef.current);
    chartInstanceRef.current = chartInstance;

    chartInstance.setOption({
      title: {
        text: `Vibration - ${deviceName}`,
        left: "center",
        top: "2%",
        textStyle: {
          fontSize: 16,
        },
      },
      series: [
        {
          type: "gauge",
          startAngle: 180,
          endAngle: 0,
          min: toLog(MIN_G),
          max: Math.log10(maxG),
          splitNumber: 4,
          axisLine: {
            lineStyle: {
              width: 10,
              color: [
                [0.3, "#67e0e3"],
                [0.7, "#37a2da"],
                [1, "#fd666d"],
              ],
            },
          },
          axisLabel: {
            formatter: (value) => formatG(10 ** value),
          },
          pointer: {
            itemStyle: {
              color: "auto",
            },
          },
          title: {
            offsetCenter: [0, "-30%"],
            fontSize: 14,
          },
          detail: {
            valueAnimation: true,
            formatter: (value) => formatG(10 ** value),
            fontSize: 20,
          },
          data: [
            {
              value: toLog(gValue),
              name: "RMS (log scale)",
            },
          ],
        },
      ],
    });

    return () => {
      chartInstance.dispose();
    };
    // gValue is applied by the update effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceName, maxG]);

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.setOption({
        series: [
          {
            data: [
              {
                value: toLog(gValue),
                name: "RMS (log scale)",
              },
            ],
          },
        ],
      });
    }
  }, [gValue]);

  return (
    <Box
      ref={chartRef}
      style={{ width: "100%", height: "400px" }}
    ></Box>
  );
}

export default VibrationGauge;
